import React, { useRef, useState, useEffect, useCallback } from "react";
import "./ActiveFiresTimeline.css";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatLabel = (date) => {
  const today = new Date();
  const time = date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (isSameDay(date, today)) return `Aujourd'hui ${time}`;
  return (
    date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) +
    ` ${time}`
  );
};

const ActiveFiresTimeline = ({
  hours,
  cursor,
  onCursorChange,
  buckets,
  onToggleLayer,
  isLayerActive,
}) => {
  const trackRef = useRef(null);
  const intervalRef = useRef(null);
  const isDraggingRef = useRef(false);
  const prevBucketsRef = useRef(buckets);
  const [isPlaying, setIsPlaying] = useState(false);

  const isTimelineDisabled = hours <= 1;

  const stopPlayback = useCallback(() => {
    clearInterval(intervalRef.current);
    setIsPlaying(false);
  }, []);

  // Whenever the underlying dataset actually changes (new preset, new
  // custom interval, cleared-back-to-"maintenant", etc.) — stop any
  // running playback. Without this, a stale interval keeps advancing
  // the cursor against the OLD hours/buckets after the user switches,
  // producing the "previous timeline didn't disappear" behavior.
  useEffect(() => {
    if (prevBucketsRef.current !== buckets) {
      stopPlayback();
      prevBucketsRef.current = buckets;
    }
  }, [buckets, stopPlayback]);
 useEffect(() => {
    if (!isLayerActive && isPlaying) {
      stopPlayback();
    }
  }, [isLayerActive, isPlaying, stopPlayback]);
  const ratioToIndex = useCallback(
    (clientX) => {
      if (!trackRef.current) return 0;
      const rect = trackRef.current.getBoundingClientRect();
      const ratio = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width),
      );
      return Math.round(ratio * (hours - 1));
    },
    [hours],
  );

  const handlePointerDown = (e) => {
    if (isTimelineDisabled) return;
    isDraggingRef.current = true;
    if (isPlaying) stopPlayback();
    onCursorChange(ratioToIndex(e.clientX));
    e.target.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    onCursorChange(ratioToIndex(e.clientX));
  };

  const handlePointerUp = () => {
    isDraggingRef.current = false;
  };

 const togglePlay = () => {
    if (isTimelineDisabled) return;

    if (isPlaying) {
      stopPlayback();
    } else {
      // Data was hidden via "Masquer" replaying should bring it back
      // and re-enable the Masquer button.
      if (!isLayerActive) {
        onToggleLayer?.();
      }
      intervalRef.current = setInterval(() => {
        onCursorChange((prev) => (prev + 1 > hours - 1 ? 0 : prev + 1));
      }, 1500);
      setIsPlaying(true);
    }
  };

  // Auto-stop if the current dataset shrinks to a single point.
  useEffect(() => {
    if (isTimelineDisabled && isPlaying) stopPlayback();
  }, [isTimelineDisabled, isPlaying, stopPlayback]);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  if (hours <= 0 || !buckets || buckets.length === 0) return null;

  // Defensive clamp: if `cursor` (owned by the parent) hasn't caught up
  // to a freshly-swapped `buckets` yet — e.g. mid-transition between
  // presets — don't read past the end of the new array for a frame.
  const safeCursor = Math.min(Math.max(cursor, 0), hours - 1);

  const pct = hours > 1 ? (safeCursor / (hours - 1)) * 100 : 100;
  const cursorTimestamp = buckets[safeCursor]?.timestamp;
  const startTimestamp = buckets[0]?.timestamp;
  const endTimestamp = buckets[buckets.length - 1]?.timestamp;

  return (
    <div className="afp-timeline">
      <div className="afp-timeline-top">
        <span className="afp-label">Rejouer la période</span>
        <span className="afp-timeline-time">
          {cursorTimestamp && formatLabel(cursorTimestamp)}
        </span>
      </div>
      <div className="afp-timeline-row">
        <div
          onClick={togglePlay}
          className="afp-timeline-play"
          style={{ cursor: isTimelineDisabled ? "not-allowed" : "pointer" }}
          data-testid="afp-timeline-play-pause-button"
          aria-disabled={isTimelineDisabled}
        >
          {isPlaying ? (
            <PauseCircleIcon
              className="afp-timeline-play-icon"
              data-testid="afp-timeline-pause-icon"
            />
          ) : (
            <PlayCircleFilledWhiteIcon
              className="afp-timeline-play-icon"
              data-testid="afp-timeline-play-icon"
            />
          )}
        </div>
        <div
          className="afp-timeline-track"
          ref={trackRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{ touchAction: "none" }}
        >
          <div className="afp-timeline-rail" />
          <div className="afp-timeline-fill" style={{ width: `${pct}%` }} />
          <div className="afp-timeline-head" style={{ left: `${pct}%` }} />
        </div>
      </div>
      <div className="afp-timeline-labels">
        <span>{startTimestamp && formatLabel(startTimestamp)}</span>
        <span>{endTimestamp && formatLabel(endTimestamp)}</span>
      </div>
    </div>
  );
};

export default React.memo(ActiveFiresTimeline);
